import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Wizard } from 'react-use-wizard';
import { START_TRIAL_MODAL } from '../../../../components/modal/StartTrialModal/StartTrialModal';
import {
  getUserProfile,
  hideModal,
  showModal,
} from '../../../../redux/actions';
import { client } from '../../../../utilities/api';
import {
  getUser,
  isImpersonating,
  setUser,
} from '../../../../utilities/storage';
import {
  isUserAnonymous,
  userHasValidMembership,
} from '../../../../utilities/user';
import { onboardingConstants } from './constants';
import Profession from './Profession';
import { OnboardingProvider, useOnboarding } from './reducer';
import Welcome from './Welcome';
import { useRedirect } from '../../../../hooks/useRedirect';
import routes from '../../../../routes/constants';
import { rtkApi } from '../../../../redux/rtk';
import T from 'prop-types';
import text from '../../../../text';
import { isRouteIncluded } from '../../../../utilities/routes';

const Onboarding = ({ submitButtonText }) => {
  const [, onboardingDispatch] = useOnboarding();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const redirect = useRedirect();

  const { userProfile, userProfileState } = useSelector((state) => ({
    userProfile: state.profileReducer.userProfile,
    userProfileState: state.profileReducer.userProfileState,
  }));

  const isUserLoggedIn = useSelector(
    (state) => state.profileReducer.isUserLoggedIn
  );

  const hasOnboarded = !!userProfile.full_name && !!userProfile.profession;
  const isEmbedRoute = isRouteIncluded(['/embed'])(window.location.pathname);

  useEffect(() => {
    if (
      isUserLoggedIn &&
      !hasOnboarded &&
      !isUserAnonymous(userProfile) &&
      !isEmbedRoute // ensure that Onboarding modal won't show up on /embed route
    ) {
      if (userProfile?.full_name) {
        onboardingDispatch({
          type: onboardingConstants.SET_FULL_NAME,
          payload: userProfile.full_name,
        });
      }

      if (!isImpersonating()) {
        setOpen(true);
      }
    }
  }, [
    userProfile,
    isUserLoggedIn,
    hasOnboarded,
    userProfileState,
    onboardingDispatch,
    isEmbedRoute,
  ]);

  const onSubmit = async (userDetails) => {
    try {
      await client.onboardUser(
        {
          fullName: userDetails.fullName,
          phoneNumber: userDetails.phoneNumber,
          profession: userDetails.profession,
          isMarketingEnabled: userDetails.isMarketingEnabled,
        },
        process.env.STRIPE_ONBOARD_COUPON_ID // always grant New Customer Discount
      );

      // legacy action to get user profile (refresh)
      await dispatch(getUserProfile());

      // Invalidate the User tag in RTK Query cache
      dispatch(rtkApi.util.invalidateTags(['User']));

      setOpen(false);
      if (!userHasValidMembership(userProfile)) {
        const currentHost = window.location.host;
        let viewerHost = '';

        try {
          const viewerUrl = new URL(process.env.LARKI_VIEWER_URL);
          viewerHost = viewerUrl.host;
        } catch (error) {
          console.error('Invalid LARKI_VIEWER_URL format:', error);
        }

        const is3DViewerPage = currentHost === viewerHost;
        // if is3DViewerPage is true, use 3D Viewer specific modal configuration
        dispatch(
          showModal(START_TRIAL_MODAL, {
            ...(is3DViewerPage && {
              onHide: () => {
                redirect.push(routes.dashboard);
                dispatch(hideModal(START_TRIAL_MODAL));
              },
              onStartTrial: () => {
                dispatch(hideModal(START_TRIAL_MODAL));
              },
            }),
          })
        );
      }

      const user = getUser();
      user['full_name'] = userDetails.fullName;
      setUser(user);
    } catch {
      setOpen(false);
    }
  };

  const onHide = () => {
    setOpen(false);
  };

  return (
    <Modal
      id='onboarding-modal'
      show={open}
      onHide={onHide}
      backdrop='static'
      keyboard={false}
      centered
    >
      <Wizard>
        <Welcome />
        <Profession onSubmit={onSubmit} submitButtonText={submitButtonText} />
      </Wizard>
    </Modal>
  );
};

Onboarding.propTypes = {
  submitButtonText: T.string,
};

const WithOnboardingProvider = ({ submitButtonText }) => {
  const currentPath = window.location.pathname;
  const isMpRoute = isRouteIncluded([routes.mp.root])(currentPath);
  const isFileManagerRoute = isRouteIncluded([routes.fileManager.root])(
    currentPath
  );

  let buttonText = submitButtonText;
  if (isMpRoute) {
    buttonText = `Continue to ${text('map2D')}`;
  } else if (isFileManagerRoute) {
    buttonText = `Continue to ${text('fileManager')}`;
  }

  return (
    <OnboardingProvider>
      <Onboarding submitButtonText={buttonText} />
    </OnboardingProvider>
  );
};

WithOnboardingProvider.propTypes = {
  submitButtonText: T.string,
};

WithOnboardingProvider.defaultProps = {
  submitButtonText: text('goToDashboard'),
};

export default WithOnboardingProvider;
